import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Rocket, TrendingUp, Target, Instagram, Youtube, Music2 } from "lucide-react";
import api from "../lib/apiClient";

const IMPACT = { "élevé": "text-emerald-400", "moyen": "text-amber-400", "faible": "text-zinc-400" };

export default function Growth() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/growth").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="growth-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Rocket className="w-7 h-7 text-amber-400" /> Growth</h1>
        <p className="text-zinc-500 text-sm mt-1">Ton plan de croissance d'audience, priorisé et organique.</p>
      </div>

      <div className="bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30 rounded-3xl p-5" data-testid="growth-diagnostic">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Diagnostic</span>
        <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{data.diagnostic}</p>
        {data.priority_channel && (
          <div className="mt-3 flex items-start gap-2 text-sm text-amber-200/90"><Target className="w-4 h-4 mt-0.5 shrink-0" /><span>{data.priority_channel}</span></div>
        )}
      </div>

      <div data-testid="growth-playbook">
        <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400" /> Playbook</h2>
        <div className="space-y-3">
          {(data.playbook || []).map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={`growth-step-${i}`}>
              <div className="flex items-start gap-3">
                <span className="font-display font-extrabold text-lg text-amber-400 w-6 text-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{s.step}</p>
                  <p className="text-xs text-zinc-500 mt-1">{s.why}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    {s.channel && <span className="px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-300">{s.channel}</span>}
                    <span className={IMPACT[s.impact] || "text-zinc-400"}>Impact {s.impact}</span>
                    <span className="text-zinc-500">Effort {s.effort}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {data.weekly_targets?.length > 0 && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="growth-targets">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Objectifs de la semaine</span>
          <ul className="mt-3 space-y-2">
            {data.weekly_targets.map((t, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-zinc-300"><span className="text-amber-400">◆</span> {t}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-3" data-testid="growth-socials">
        {data.socials?.instagram && <Social icon={Instagram} label="Instagram" />}
        {data.socials?.tiktok && <Social icon={Music2} label="TikTok" />}
        {data.socials?.youtube && <Social icon={Youtube} label="YouTube" />}
        {data.socials?.spotify && <Social icon={Music2} label="Spotify" />}
      </div>
    </div>
  );
}

function Social({ icon: Icon, label }) {
  return <span className="inline-flex items-center gap-2 px-3 h-9 rounded-xl bg-[#18181E] border border-zinc-800 text-sm text-zinc-400"><Icon className="w-4 h-4" /> {label} connecté</span>;
}
